/**
 * Public form renderer — ``/forms/$flowId`` (no auth).
 *
 * Fetches a published flow's form schema and renders a fillable form; on submit
 * it starts the flow with the entered values. Standalone layout (not the
 * workspace shell) so it's shareable with people outside the org.
 */

import { type FormField, getPublicForm, submitPublicForm } from "@ando/core";
import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Send } from "lucide-react";
import * as React from "react";

export const Route = createFileRoute("/forms/$flowId")({
  component: PublicFormRoute,
});

function PublicFormRoute(): React.JSX.Element {
  const { i18n } = useLingui();
  const { flowId } = Route.useParams();
  const {
    data: form,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["public-form", flowId],
    queryFn: () => getPublicForm(flowId),
    retry: false,
  });
  const [values, setValues] = React.useState<Record<string, unknown>>({});
  const [done, setDone] = React.useState(false);

  const submit = useMutation({
    mutationFn: () => submitPublicForm(flowId, values),
    onSuccess: () => setDone(true),
  });

  const setVal = (key: string, v: unknown) => setValues((s) => ({ ...s, [key]: v }));

  return (
    <div className="flex min-h-dvh items-center justify-center bg-cream px-4 py-10">
      <div className="w-full max-w-md rounded-[16px] border border-hairline bg-white p-6 shadow-sm">
        {isLoading ? (
          <p className="text-center text-[13px] text-ink-3">{i18n._(msg`Caricamento…`)}</p>
        ) : isError || !form ? (
          <p className="text-center text-[13px] text-ink-3">
            {i18n._(msg`Questo modulo non esiste o non è più pubblicato.`)}
          </p>
        ) : done ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 size={36} className="text-emerald-500" aria-hidden />
            <p className="font-medium text-[15px] text-ink-1">{i18n._(msg`Inviato, grazie!`)}</p>
            <p className="text-[12.5px] text-ink-3">
              {i18n._(msg`La tua richiesta è stata registrata.`)}
            </p>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit.mutate();
            }}
            className="flex flex-col gap-3"
          >
            <div className="mb-1">
              <h1 className="font-semibold text-[18px] text-ink-1">
                {form.title || i18n._(msg`Modulo`)}
              </h1>
              {form.description ? (
                <p className="mt-1 text-[13px] text-ink-3">{form.description}</p>
              ) : null}
            </div>
            {form.fields.map((f: FormField) => (
              <div key={f.key} className="flex flex-col gap-1">
                <label htmlFor={`ff_${f.key}`} className="font-medium text-[12px] text-ink-2">
                  {f.label}
                  {f.required ? <span className="ml-0.5 text-ando-orange">*</span> : null}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    id={`ff_${f.key}`}
                    required={f.required}
                    placeholder={f.placeholder}
                    rows={3}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    className="w-full resize-y rounded-md border border-hairline bg-white px-2.5 py-1.5 text-[13px] text-ink-1 focus:border-ando-orange focus:outline-none"
                  />
                ) : (
                  <input
                    id={`ff_${f.key}`}
                    type={f.type === "email" ? "email" : f.type === "number" ? "number" : "text"}
                    required={f.required}
                    placeholder={f.placeholder}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    className="w-full rounded-md border border-hairline bg-white px-2.5 py-1.5 text-[13px] text-ink-1 focus:border-ando-orange focus:outline-none"
                  />
                )}
              </div>
            ))}
            {submit.isError ? (
              <p className="text-[12px] text-rose-500">
                {i18n._(msg`Invio non riuscito, riprova.`)}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submit.isPending}
              className={cn(
                "mt-1 inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 font-medium text-[13px] text-white transition-colors",
                "bg-ando-orange hover:bg-ando-orange/90 disabled:opacity-50",
              )}
            >
              <Send size={14} aria-hidden />
              {submit.isPending ? i18n._(msg`Invio…`) : i18n._(msg`Invia`)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
